import React, { useMemo, useState, useEffect, useRef } from 'react';
import api from '../api';
import { Send, AlertCircle, Clock, CheckCircle, Shield, MapPin, ExternalLink } from 'lucide-react';
import './modalStyles.css';

const UserInbox = ({ onClose, reportId = null, onViewReport = null }) => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sending, setSending] = useState(false);
  const [activeThreadReportId, setActiveThreadReportId] = useState(reportId ?? null);
  const responseRef = useRef(null);

  useEffect(() => {
    loadMessages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  useEffect(() => {
    setActiveThreadReportId(reportId ?? null);
    setSelectedMessage(null);
  }, [reportId]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/user/messages');
      const allMessages = res.data || [];
      const filtered = reportId ? allMessages.filter(m => m && m.reportId === reportId) : allMessages;
      setMessages(filtered);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await api.put(`/api/user/messages/${messageId}/read`);
      loadMessages();
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleRespond = async (messageId) => {
    const responseText = responseRef.current?.value?.trim();
    if (!responseText) {
      setNotice('');
      setError('Please enter a response');
      return;
    }

    setSending(true);
    setError('');
    setNotice('');
    try {
      await api.post(`/api/user/messages/${messageId}/respond`, { response: responseText });
      setNotice('Response sent successfully');
      setSelectedMessage(null);
      if (responseRef.current) responseRef.current.value = '';
      loadMessages();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send response');
    } finally {
      setSending(false);
    }
  };

  const openMessage = (msg) => {
    setSelectedMessage(msg);
    if (!msg.isRead) {
      handleMarkAsRead(msg.id);
    }
  };

  const formatCoords = (lat, lng) => {
    if (typeof lat !== 'number' || typeof lng !== 'number') return '';
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  };

  const normalizedMessages = useMemo(() => {
    return (messages || []).map(m => {
      const rid = m.reportId ?? null;
      return {
        ...m,
        __threadReportId: rid,
        __threadKey: rid ? `report:${rid}` : 'general',
        __threadLabel: rid
          ? (m.reportAddress || `Report ${rid}`)
          : 'General'
      };
    });
  }, [messages]);

  const threadSummaries = useMemo(() => {
    const map = new Map();
    normalizedMessages.forEach(m => {
      const key = m.__threadKey;
      if (!map.has(key)) {
        map.set(key, {
          key,
          reportId: m.__threadReportId,
          label: m.__threadLabel,
          reportAddress: m.reportAddress,
          reportLat: m.reportLat,
          reportLng: m.reportLng,
          lastTimestamp: m.timestamp,
          unreadCount: m.isRead ? 0 : 1,
          count: 1
        });
        return;
      }
      const s = map.get(key);
      s.count += 1;
      s.unreadCount += m.isRead ? 0 : 1;
      if (!s.lastTimestamp || new Date(m.timestamp).getTime() > new Date(s.lastTimestamp).getTime()) {
        s.lastTimestamp = m.timestamp;
      }
    });
    const list = Array.from(map.values());
    list.sort((a, b) => new Date(b.lastTimestamp).getTime() - new Date(a.lastTimestamp).getTime());
    return list;
  }, [normalizedMessages]);

  const visibleMessages = useMemo(() => {
    if (reportId) {
      return normalizedMessages.filter(m => m.reportId === reportId);
    }
    if (activeThreadReportId === null) {
      return normalizedMessages.filter(m => m.__threadReportId === null);
    }
    if (!activeThreadReportId) {
      return normalizedMessages;
    }
    return normalizedMessages.filter(m => m.reportId === activeThreadReportId);
  }, [normalizedMessages, reportId, activeThreadReportId]);

  useEffect(() => {
    if (reportId) return;
    if (activeThreadReportId !== null && activeThreadReportId !== undefined) return;
    const first = threadSummaries[0];
    if (first) {
      setActiveThreadReportId(first.reportId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadSummaries]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><Shield size={20} /> Safety Team Messages</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {notice && (
            <div className="alert alert-success">
              <CheckCircle size={16} />
              <span>{notice}</span>
            </div>
          )}

          {loading ? (
            <div className="loading">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="empty-state">
              <Shield size={48} style={{ opacity: 0.3 }} />
              <p>{reportId ? 'No messages for this report' : 'No messages from Safety Team'}</p>
            </div>
          ) : (
            <>
              {!reportId && threadSummaries.length > 1 && (
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px', marginBottom: '12px' }}>
                  {threadSummaries.map(t => {
                    const active = (activeThreadReportId ?? null) === (t.reportId ?? null);
                    const coords = formatCoords(t.reportLat, t.reportLng);
                    const label = t.reportId
                      ? (t.reportAddress || `Report ${t.reportId}`)
                      : 'General';
                    return (
                      <button
                        key={t.key}
                        className="action-btn"
                        onClick={() => {
                          setActiveThreadReportId(t.reportId ?? null);
                          setSelectedMessage(null);
                        }}
                        style={{
                          whiteSpace: 'nowrap',
                          background: active ? '#97AC82' : '#f8f9fa',
                          color: active ? 'white' : '#333',
                          borderRadius: '999px',
                          padding: '6px 10px',
                          fontSize: '12px',
                          border: '1px solid #eaeaea'
                        }}
                        title={coords ? `${label} (${coords})` : label}
                      >
                        {label}{t.unreadCount > 0 ? ` (${t.unreadCount})` : ''}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="messages-inbox">
              {visibleMessages.map(msg => (
                <div 
                  key={msg.id} 
                  className={`inbox-message ${!msg.isRead ? 'unread' : ''}`}
                  onClick={() => openMessage(msg)}
                >
                  <div className="message-preview">
                    <div className="message-header-row">
                      <span className="from-moderator">
                        <Shield size={14} style={{marginRight: '6px', verticalAlign: 'middle'}} />
                        Safety Team
                      </span>
                      <span className={`status-badge ${msg.isRead ? 'read' : 'unread'}`}>
                        {msg.isRead ? 'read' : 'new'}
                      </span>
                    </div>
                    {msg.reportId && (
                      <div style={{
                        fontSize: '11px',
                        color: '#666',
                        backgroundColor: '#f0f0f0',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        marginBottom: '6px'
                      }}>
                        Re: {msg.reportType || 'Report'} - {msg.reportDescription?.substring(0, 40) || 'Your report'}{msg.reportDescription?.length > 40 ? '...' : ''}
                      </div>
                    )}

                    {msg.reportId && (msg.reportAddress || (typeof msg.reportLat === 'number' && typeof msg.reportLng === 'number')) && (
                      <div style={{
                        fontSize: '11px',
                        color: '#555',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '6px'
                      }}>
                        <MapPin size={12} />
                        <span>
                          {msg.reportAddress || 'Location'}
                          {(typeof msg.reportLat === 'number' && typeof msg.reportLng === 'number')
                            ? ` (${formatCoords(msg.reportLat, msg.reportLng)})`
                            : ''}
                        </span>
                      </div>
                    )}
                    <p className="message-text">{msg.message?.substring(0, 100) || ''}{msg.message?.length > 100 ? '...' : ''}</p>
                    <div className="message-meta">
                      <Clock size={12} />
                      <span>{new Date(msg.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      </div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="modal-overlay" onClick={() => setSelectedMessage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><Shield size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />Message from Safety Team</h3>
              <button className="modal-close" onClick={() => setSelectedMessage(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="message-detail">
                <div className="message-type">
                  <span className={`type-badge ${selectedMessage.requestType}`}>
                    {selectedMessage.requestType === 'info' ? 'Information' : 
                     selectedMessage.requestType === 'warning' ? 'Warning' : 
                     selectedMessage.requestType === 'clarification' ? 'Clarification Needed' : 
                     selectedMessage.requestType}
                  </span>
                </div>
                
                {selectedMessage.reportId && (
                  <div style={{
                    backgroundColor: '#e9ecef',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    marginBottom: '15px',
                    fontSize: '13px',
                    borderLeft: '3px solid #6c757d'
                  }}>
                    <strong>Regarding Your Report:</strong><br/>
                    <span style={{ color: '#495057' }}>
                      {selectedMessage.reportType || 'Report'} - {selectedMessage.reportDescription || 'Your submitted report'}
                    </span>

                    {(selectedMessage.reportAddress || (typeof selectedMessage.reportLat === 'number' && typeof selectedMessage.reportLng === 'number')) && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <MapPin size={14} />
                        <span>
                          {selectedMessage.reportAddress || 'Location'}
                          {(typeof selectedMessage.reportLat === 'number' && typeof selectedMessage.reportLng === 'number')
                            ? ` (${formatCoords(selectedMessage.reportLat, selectedMessage.reportLng)})`
                            : ''}
                        </span>
                        {typeof onViewReport === 'function' && (
                          <button
                            className="action-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onViewReport({
                                reportId: selectedMessage.reportId,
                                lat: selectedMessage.reportLat,
                                lng: selectedMessage.reportLng
                              });
                              onClose();
                            }}
                            style={{
                              marginLeft: 'auto',
                              background: '#ffffff',
                              border: '1px solid #dcdcdc',
                              color: '#333',
                              padding: '6px 10px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <ExternalLink size={14} /> View on map
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="message-content">
                  <p>{selectedMessage.message}</p>
                </div>

                <div className="message-timestamp">
                  <Clock size={14} />
                  {new Date(selectedMessage.timestamp).toLocaleString()}
                </div>

                {selectedMessage.userResponse && (
                  <div className="user-response-display">
                    <h4>Your Response:</h4>
                    <p>{selectedMessage.userResponse}</p>
                    <div className="response-timestamp">
                      <CheckCircle size={14} />
                      Responded on {new Date(selectedMessage.respondedAt).toLocaleString()}
                    </div>
                  </div>
                )}

                {!selectedMessage.userResponse && (
                  <div className="response-form">
                    <h4>Send Response:</h4>
                    <textarea
                      ref={responseRef}
                      defaultValue=""
                      placeholder="Type your response..."
                      rows={4}
                    />
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleRespond(selectedMessage.id)}
                      disabled={sending}
                    >
                      <Send size={14} /> {sending ? 'Sending...' : 'Send Response'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .messages-inbox {
          max-height: 500px;
          overflow-y: auto;
        }

        .inbox-message {
          padding: 15px;
          border-bottom: 1px solid #eee;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .inbox-message:hover {
          background-color: #f8f9fa;
        }

        .inbox-message.unread {
          background-color: #e3f2fd;
          font-weight: 500;
        }

        .message-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .from-moderator {
          font-weight: 600;
          color: #333;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          text-transform: uppercase;
        }

        .status-badge.unread {
          background-color: #2196f3;
          color: white;
        }

        .status-badge.read {
          background-color: #9e9e9e;
          color: white;
        }

        .status-badge.responded {
          background-color: #4caf50;
          color: white;
        }

        .message-text {
          margin: 8px 0;
          color: #666;
        }

        .message-meta {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #999;
        }

        .message-detail {
          padding: 10px 0;
        }

        .type-badge {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 15px;
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          margin-bottom: 15px;
        }

        .type-badge.info {
          background-color: #2196f3;
          color: white;
        }

        .type-badge.warning {
          background-color: #ff9800;
          color: white;
        }

        .type-badge.clarification {
          background-color: #9c27b0;
          color: white;
        }

        .message-content {
          background-color: #f5f5f5;
          padding: 15px;
          border-radius: 8px;
          margin: 15px 0;
        }

        .message-timestamp {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #999;
          margin-bottom: 20px;
        }

        .user-response-display {
          margin-top: 20px;
          padding: 15px;
          background-color: #e8f5e9;
          border-radius: 8px;
        }

        .user-response-display h4 {
          margin: 0 0 10px 0;
          color: #2e7d32;
        }

        .user-response-display p {
          margin: 0 0 10px 0;
        }

        .response-timestamp {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #2e7d32;
        }

        .response-form {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }

        .response-form h4 {
          margin: 0 0 10px 0;
        }

        .response-form textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          resize: vertical;
          font-family: inherit;
          margin-bottom: 10px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #999;
        }

        .empty-state p {
          margin-top: 15px;
          font-size: 16px;
        }

        .modal-content.large {
          max-width: 700px;
          width: 90%;
        }
      `}</style>
    </div>
  );
};

export default UserInbox;
